import { Box, Button, paletteColors, TextField, Typography } from '@evaneos/b2b-ui';
import UnfoldIcon from '@evaneos/b2b-ui/icons/UnfoldMoreOutlined';
import UnfoldLessIcon from '@evaneos/b2b-ui/icons/UnfoldLessOutlined';
import { FormEvent } from 'react';
import { useIntl } from 'react-intl';
import DOMPurify from 'dompurify';

import { DossierId, Memo } from '../../../../resources/dossier/types';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { toSafeHtml } from '../../../Dossier/Discussion/utils/messageHtmlConverter';
import { useSidePanel } from '../../SidePanel/SidePanelContext';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

import { useMemoEditor } from './useMemoEditor';

/**
 * Extracts plain text from HTML content for editing.
 * Formatted memos will display with their formatting, but when edited,
 * users see/edit plain text only (formatting is lost on save).
 *
 * Preserves line breaks by converting block elements (<p>, <div>, <br>) to newlines.
 */
export const extractPlainTextFromHtml = (html: string | null | undefined): string => {
    if (!html) return '';

    const trimmedHtml = html.trim();

    // Check if content looks like HTML
    if (/<\/?[a-z][\s\S]*>/i.test(trimmedHtml)) {
        const htmlWithNewlines = trimmedHtml
            .replace(/<\/p>/gi, '\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<\/li>/gi, '\n')
            .replace(/<br\s*\/?>/gi, '\n');

        const plainText = DOMPurify.sanitize(htmlWithNewlines, {
            ALLOWED_TAGS: [],
            KEEP_CONTENT: true,
        });

        return plainText
            .replace(/\n{3,}/g, '\n\n')
            .replace(/&nbsp;/g, ' ')
            .trim();
    }

    return trimmedHtml;
};

type MemoEditableItemProps = {
    memo: Memo;
    dossierId: DossierId;
    labelId: string;
};

export function MemoEditableItem({ memo, dossierId, labelId }: Readonly<MemoEditableItemProps>) {
    const { formatMessage } = useIntl();
    const { openPanel, closePanel, isOpen, panelState } = useSidePanel();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const {
        localValue: memoValue,
        handleChange: handleMemoChange,
        lastSavedValueRef,
        changeMemoMutation,
        handleFocus: handleMemoFocus,
        handleBlur: handleMemoBlur,
    } = useMemoEditor({ dossierId, memo });

    const isNoteEditorOpen = isOpen && panelState.type === 'note-editor';

    const handleBeforeClose = () => {
        const currentMemoValue = memoValue.trim();

        if (currentMemoValue !== lastSavedValueRef.current && !changeMemoMutation.isPending) {
            changeMemoMutation.mutate({ memo: currentMemoValue || null });
            lastSavedValueRef.current = currentMemoValue;
        }

        return true;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>, close: () => void) => {
        e.preventDefault();
        close();
    };

    const handleTogglePanel = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isNoteEditorOpen) {
            closePanel();
            trackEvent((ampli, defaultProperties) => {
                ampli.memoClosed({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        } else {
            openPanel({ contentType: 'note-editor', data: { dossierId } });
            trackEvent((ampli, defaultProperties) => {
                ampli.memoExpanded({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }
    };

    return (
        <DefinitionList.EditableValue
            labelId={labelId}
            popoverWidth="trigger"
            placement="over"
            onBeforeClose={handleBeforeClose}
            disablePopoverMinHeight
            showHoverActionOnTouch
            hoverAction={
                <Button
                    size="small"
                    variant="outlined"
                    onClick={handleTogglePanel}
                    startIcon={
                        isNoteEditorOpen ? (
                            <UnfoldLessIcon sx={{ transform: 'rotate(45deg)' }} />
                        ) : (
                            <UnfoldIcon sx={{ transform: 'rotate(45deg)' }} />
                        )
                    }
                    sx={{ fontSize: '1.4rem', maxHeight: 28 }}
                >
                    {formatMessage({
                        id: isNoteEditorOpen
                            ? 'dossier.memo.collapse_button'
                            : 'dossier.memo.expand_button',
                    })}
                </Button>
            }
            editor={({ close }) => (
                <Box
                    component="form"
                    position="relative"
                    onSubmit={(e) => handleSubmit(e, close)}
                    sx={{
                        border: '1px solid',
                        borderRadius: '4px',
                        borderColor: 'primary.main',
                        '&:hover': {
                            borderColor: paletteColors.neutral[500],
                        },
                        '&:active': {
                            borderColor: paletteColors.neutral[300],
                        },
                    }}
                >
                    <TextField
                        name="memo"
                        type="text"
                        multiline
                        minRows={5}
                        maxRows={10}
                        slotProps={{
                            input: {
                                sx: {
                                    padding: 0,
                                },
                            },
                            htmlInput: {
                                'data-testid': 'input-memo',
                                'aria-labelledby': labelId,
                                sx: {
                                    padding: 2,
                                    fontSize: '1.4rem',
                                },
                            },
                        }}
                        size="small"
                        value={memoValue}
                        onChange={handleMemoChange}
                        onFocus={handleMemoFocus}
                        onBlur={handleMemoBlur}
                        placeholder={formatMessage({ id: 'form.empty_placeholder' })}
                        fullWidth
                        autoFocus
                    />
                </Box>
            )}
        >
            {memo ? (
                <Typography
                    component="span"
                    variant="body2"
                    dangerouslySetInnerHTML={{
                        __html: toSafeHtml(memo),
                    }}
                    sx={{
                        whiteSpace: 'pre-wrap',
                        maxHeight: '200px',
                        display: 'block',
                        overflowY: 'auto',
                        '& > p': {
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            lineHeight: '20px',
                        },
                        '& ul, & ol': {
                            listStyle: 'disc',
                            padding: 1,
                            listStylePosition: 'inside',
                            fontFamily: 'inherit',
                        },
                    }}
                />
            ) : (
                <Typography variant="body2" color="text.secondary" component="span">
                    {formatMessage({ id: 'form.empty_placeholder' })}
                </Typography>
            )}
        </DefinitionList.EditableValue>
    );
}
